import { useCallback, useEffect } from 'react'
import { UseData } from './useData'
import { useFormikContext } from 'formik'
import { ILeadFormModel } from '../../typings'
import { getCallActivityByPhone } from '../../functions/getCallActivityByPhone'
import { PrefillCallTrackingData } from '../../PrefillCallTrackingData'
import { formatInitialPhoneNumberValue } from '../../initialValues'
import { toast } from '@sales-center/components'
import { AddLeadProps } from '../MainForm'
import { useQueryClient } from 'react-query'
import { ICallTrackingActivity } from '@user/features/CampaignTracking/typings'

type Props = Pick<
  UseData,
  | 'campaigns'
  | 'defaultSalesTeam'
  | 'divisions'
  | 'defaultFunnel'
  | 'firstStage'
  | 'leadSources'
  | 'loadSalesTeamUsers'
  | 'locationTypes'
> &
  Pick<AddLeadProps, 'isDraftLead' | 'draftLead'>

export function useEffects(props: Props) {
  const {
    campaigns,
    defaultSalesTeam,
    divisions,
    draftLead,
    defaultFunnel,
    firstStage,
    isDraftLead,
    leadSources,
    loadSalesTeamUsers,
    locationTypes,
  } = props

  const { values, setFieldValue, setValues, errors } =
    useFormikContext<ILeadFormModel>()

  const {
    assigneeId,
    callTrackingActivity,
    divisionId,
    salesFunnelId,
    salesTeamId,
    locationTypeId,
    altPhoneNumber,
    phoneNumber,
    salesFunnelStageId,
  } = values

  //#region effects
  // load sales team users when team changes
  useEffect(() => {
    if (salesTeamId != '') loadSalesTeamUsers(+salesTeamId)
  }, [loadSalesTeamUsers, salesTeamId])

  useEffect(() => {
    if (callTrackingActivity !== null && callTrackingActivity !== undefined)
      PrefillCallTrackingData(
        setValues,
        callTrackingActivity,
        leadSources,
        campaigns,
      )
  }, [callTrackingActivity, setValues, leadSources, campaigns])

  const checkCallTrackingActivity = useCallback(async () => {
    const hyphenExp = new RegExp('[-() ]', 'g')
    const normalize = (num?: string) => num?.replace(hyphenExp, '') || ''

    const normalizedPrimary = normalize(phoneNumber)
    const normalizedAlt = normalize(altPhoneNumber)
    const normalizedCurrent = normalize(callTrackingActivity?.phone)
    let phoneNumberResponse: ICallTrackingActivity | null = null
    let altPhoneNumberResponse: ICallTrackingActivity | null = null

    if (phoneNumber && !errors.phoneNumber) {
      phoneNumberResponse = await getCallActivityByPhone(normalizedPrimary)
    }

    if (altPhoneNumber && !errors.altPhoneNumber) {
      if (phoneNumber && !errors.phoneNumber) {
        if (normalizedCurrent !== normalizedPrimary) {
          altPhoneNumberResponse = await getCallActivityByPhone(normalizedAlt)
        }
      } else {
        if (
          normalizedCurrent !== normalizedAlt &&
          (!errors.phoneNumber ||
            errors.phoneNumber?.toLowerCase() === 'required')
        ) {
          altPhoneNumberResponse = await getCallActivityByPhone(normalizedAlt)
        }
      }
    }

    if (phoneNumberResponse?.phone && altPhoneNumberResponse?.phone) {
      if (normalizedCurrent !== normalizedPrimary) {
        await setFieldValue('callTrackingActivity', phoneNumberResponse)
        toast.success(
          'Phone number for Lead associated with Marketing Campaign',
        )
        return
      } else if (normalizedCurrent !== normalizedAlt) {
        await setFieldValue('callTrackingActivity', altPhoneNumberResponse)
        toast.success(
          'Phone number for Lead associated with Marketing Campaign',
        )
        return
      }
    } else {
      if (phoneNumberResponse?.phone) {
        if (normalizedCurrent !== normalizedPrimary) {
          await setFieldValue('callTrackingActivity', phoneNumberResponse)
          toast.success(
            'Primary phone number for Lead associated with Marketing Campaign',
          )
          return
        }
      }

      if (altPhoneNumberResponse?.phone) {
        if (normalizedCurrent !== normalizedAlt) {
          await setFieldValue('callTrackingActivity', altPhoneNumberResponse)
          toast.success(
            'Alternate phone number for Lead associated with Marketing Campaign',
          )
          return
        }
      }
    }

    if (
      normalizedCurrent !== normalizedPrimary &&
      normalizedCurrent !== normalizedAlt
    ) {
      await setFieldValue('callTrackingActivity', null)
    }
  }, [
    altPhoneNumber,
    callTrackingActivity?.phone,
    errors.altPhoneNumber,
    errors.phoneNumber,
    phoneNumber,
    setFieldValue,
  ])

  useEffect(() => {
    checkCallTrackingActivity()
  }, [
    phoneNumber,
    setFieldValue,
    altPhoneNumber,
    callTrackingActivity?.phone,
    errors.phoneNumber,
    errors.altPhoneNumber,
  ])

  // auto-set default funnel
  useEffect(() => {
    if (!!defaultFunnel) {
      //If it's a draft lead AND has a SalesFunnelId set in the DB
      if (isDraftLead && !!salesFunnelId)
        setFieldValue('salesFunnelId', salesFunnelId)
      //Else just bind to the default Sales Funnel (if first time), Or the chosen funnel
      else {
        const selectedFunnelId: string = !!salesFunnelId
          ? salesFunnelId
          : defaultFunnel.id
        setFieldValue('salesFunnelId', selectedFunnelId)
      }
    }
  }, [defaultFunnel, isDraftLead, salesFunnelId, setFieldValue])

  // auto-set default Sales Team and Owner, if Draft Lead
  useEffect(() => {
    if (isDraftLead && !!defaultSalesTeam) {
      //If the draft lead has a SalesTeamId set in the DB
      if (salesTeamId != '') setFieldValue('salesTeamId', +salesTeamId)
      //Else just bind to the default Sales Team
      else setFieldValue('salesTeamId', defaultSalesTeam.id)

      //Check and set the Assignee/Owner based on the Sales Team and assigneeId value
      if (!!assigneeId) setFieldValue('assigneeId', assigneeId)
      else if (assigneeId === '') {
        setFieldValue('assigneeId', 'unassigned')
      }
    }
  }, [assigneeId, defaultSalesTeam, isDraftLead, salesTeamId, setFieldValue])

  // auto-set first funnel stage
  useEffect(() => {
    if (!salesFunnelStageId && !!firstStage)
      setFieldValue('salesFunnelStageId', firstStage.id)
  }, [firstStage, salesFunnelStageId, setFieldValue])

  useEffect(() => {
    const locType = locationTypes.find((x) => x.id == locationTypeId)
    if (!!locType) setFieldValue('locationType', locType.name)
    else setFieldValue('locationType', '')
  }, [locationTypeId, locationTypes, setFieldValue])

  useEffect(() => {
    const div = divisions.find((x) => x.id == divisionId)
    if (!!div) setFieldValue('division', div.name)
    else setFieldValue('division', '')
  }, [divisionId, divisions, setFieldValue])

  //#endregion

  useEffect(() => {
    if (isDraftLead && draftLead) {
      if (!values?.externalLocationCode && !values.firstName)
        setFieldValue('firstName', draftLead?.firstName)
      if (!values?.externalLocationCode && !values.lastName)
        setFieldValue('lastName', draftLead?.lastName)
      if (!values?.externalLocationCode && !values.company)
        setFieldValue('company', draftLead?.company)
      if (values?.emailAddresses?.length == 0) {
        setFieldValue('emailAddresses[0].emailAddress', draftLead?.emailAddress)
      }
      setFieldValue('note', draftLead?.note)

      //Phone number
      const phoneNumber = formatInitialPhoneNumberValue(draftLead?.phoneNumber)
      if (phoneNumber) {
        setFieldValue('phoneNumber', phoneNumber)
      }
    }
  }, [draftLead, isDraftLead, setFieldValue, values?.emailAddresses])

  const client = useQueryClient()
  useEffect(() => void client.removeQueries(['duplicateCheck']), [client])
}
